/**
 * Tiny fetch wrapper for the telemetry Worker's `/admin/*` endpoints.
 *
 * Auth: the Worker route is behind a Cloudflare Access policy that
 * shares its cookie scope with this SPA (`.atlasdesk.co`). Requests
 * MUST go out with `credentials: "include"` so the browser sends the
 * `CF_Authorization` cookie; without it the Access edge returns the
 * SSO challenge HTML, which doesn't parse as JSON and we surface a
 * clear error to the dev.
 *
 * Origin override: in `vite dev` we hit the deployed Worker directly
 * via `VITE_API_BASE`; in production the SPA's origin is admin., the
 * Worker's is telemetry., so we always need an absolute URL.
 */
import type {
  AnalyticsEventRow,
  BugReportRow,
  ErrorEventRow,
  ErrorGroupRow,
  FeatureRequestRow,
  FeedKind,
  FeedResponse,
  SummaryResponse,
} from "./types";

const API_BASE =
  import.meta.env.VITE_API_BASE ?? "https://telemetry.atlasdesk.co";

export class AccessChallengeError extends Error {
  constructor() {
    super(
      "Cloudflare Access redirected to the SSO login. Open the SPA in your browser, complete Google sign-in, then retry.",
    );
    this.name = "AccessChallengeError";
  }
}

async function getJson<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "GET",
      credentials: "include",
      headers: { accept: "application/json" },
    });
  } catch (err) {
    throw new Error(
      `Network error talking to the telemetry Worker: ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
  // Access intercepts unauthenticated requests with a 302 to the SSO
  // page. Because of CORS the browser follows it opaquely; fetch
  // surfaces this as a non-JSON response (HTML) and `res.json()`
  // throws below. Distinguish here so the UI can guide the user.
  const contentType = res.headers.get("content-type") ?? "";
  if (res.status === 401 || res.status === 403) {
    throw new AccessChallengeError();
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText} — ${detail}`.trim());
  }
  if (!contentType.includes("application/json")) {
    throw new AccessChallengeError();
  }
  return (await res.json()) as T;
}

export const api = {
  summary(): Promise<SummaryResponse> {
    return getJson<SummaryResponse>("/admin/summary");
  },

  feed<K extends FeedKind>(
    kind: K,
    opts?: { since?: number; limit?: number },
  ): Promise<
    FeedResponse<
      K extends "bug-report"
        ? BugReportRow
        : K extends "feature-request"
          ? FeatureRequestRow
          : AnalyticsEventRow
    >
  > {
    const params = new URLSearchParams({ kind });
    if (opts?.since) params.set("since", String(opts.since));
    if (opts?.limit) params.set("limit", String(opts.limit));
    return getJson(`/admin/feed?${params.toString()}`);
  },

  attachmentUrl(key: string): string {
    return `${API_BASE}/admin/attachment/${encodeURIComponent(key)}`;
  },

  errors(opts?: {
    view?: "recent" | "groups";
    since?: number;
    limit?: number;
  }): Promise<FeedResponse<ErrorEventRow | ErrorGroupRow>> {
    const params = new URLSearchParams();
    if (opts?.view) params.set("view", opts.view);
    if (opts?.since) params.set("since", String(opts.since));
    if (opts?.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return getJson(`/admin/errors${qs ? `?${qs}` : ""}`);
  },
};
